import {
  SPOTIFY_AUTH_ENDPOINT,
  SPOTIFY_TOKEN_ENDPOINT,
  SPOTIFY_SCOPES,
  SPOTIFY_CLIENT_ID,
  SPOTIFY_REDIRECT_URI,
  LS_CODE_VERIFIER,
  LS_ACCESS_TOKEN,
  LS_REFRESH_TOKEN,
  LS_TOKEN_EXPIRY,
} from "./constants";
import type { SpotifyTokenResponse } from "./types";

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest("SHA-256", encoder.encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function redirectToSpotifyLogin(): Promise<void> {
  const codeVerifier = generateRandomString(64);
  localStorage.setItem(LS_CODE_VERIFIER, codeVerifier);

  const challenge = base64UrlEncode(await sha256(codeVerifier));

  const params = new URLSearchParams({
    response_type: "code",
    client_id: SPOTIFY_CLIENT_ID,
    scope: SPOTIFY_SCOPES,
    redirect_uri: SPOTIFY_REDIRECT_URI,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });

  window.location.href = `${SPOTIFY_AUTH_ENDPOINT}?${params.toString()}`;
}

export async function exchangeCodeForTokens(
  code: string,
): Promise<SpotifyTokenResponse> {
  const codeVerifier = localStorage.getItem(LS_CODE_VERIFIER) ?? "";

  const response = await fetch(SPOTIFY_TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      grant_type: "authorization_code",
      code,
      redirect_uri: SPOTIFY_REDIRECT_URI,
      code_verifier: codeVerifier,
    }),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const data: SpotifyTokenResponse = await response.json();
  storeTokens(data);
  localStorage.removeItem(LS_CODE_VERIFIER);
  return data;
}

export async function refreshAccessToken(): Promise<SpotifyTokenResponse> {
  const refreshToken = localStorage.getItem(LS_REFRESH_TOKEN);
  if (!refreshToken) throw new Error("No refresh token available");

  const response = await fetch(SPOTIFY_TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data: SpotifyTokenResponse = await response.json();
  storeTokens(data);
  return data;
}

function storeTokens(data: SpotifyTokenResponse): void {
  localStorage.setItem(LS_ACCESS_TOKEN, data.access_token);
  if (data.refresh_token) {
    localStorage.setItem(LS_REFRESH_TOKEN, data.refresh_token);
  }
  const expiresAt = Date.now() + data.expires_in * 1000;
  localStorage.setItem(LS_TOKEN_EXPIRY, expiresAt.toString());
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(LS_ACCESS_TOKEN);
}

export function isTokenExpired(): boolean {
  const expiry = localStorage.getItem(LS_TOKEN_EXPIRY);
  if (!expiry) return true;
  return Date.now() >= parseInt(expiry, 10);
}

export function getTokenExpiryMs(): number {
  const expiry = localStorage.getItem(LS_TOKEN_EXPIRY);
  if (!expiry) return 0;
  return parseInt(expiry, 10) - Date.now();
}

export function clearTokens(): void {
  localStorage.removeItem(LS_ACCESS_TOKEN);
  localStorage.removeItem(LS_REFRESH_TOKEN);
  localStorage.removeItem(LS_TOKEN_EXPIRY);
  localStorage.removeItem(LS_CODE_VERIFIER);
}
